import React, { useRef, useState } from 'react';
import { Download, Copy, Check, AlertTriangle, Image as ImageIcon } from 'lucide-react';
import * as htmlToImage from 'html-to-image';

/**
 * Problem-solver ad — "We own the clean up the mess market".
 *
 * Lead with the pain (missing tags, NAIT mismatches, records that don't match
 * the yard). Sell the outcome — clean herd data, sorted in one visit.
 *
 * Two formats:
 *   1) Portrait:  1080 × 1350
 *   2) Landscape: 1200 × 630
 */
const HerdDataSortedAd = () => {
  const portraitRef = useRef(null);
  const landscapeRef = useRef(null);
  const [downloading, setDownloading] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleDownload = async (ref, filename) => {
    if (!ref.current) return;
    setDownloading(filename);
    try {
      const dataUrl = await htmlToImage.toPng(ref.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#0f2818',
      });
      const link = document.createElement('a');
      link.download = filename;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Download failed:', err);
      alert('Could not generate the PNG. Try a different browser if it persists.');
    } finally {
      setDownloading(null);
    }
  };

  const adCopy = `Herd data headaches? Sorted in one visit.

Missing tags. NAIT mismatches. Records that don't line up with what's actually in the yard. It's the admin every dairy and beef farmer puts off — until it costs you time, money or compliance.

Measured Livestock specialises in cleaning up the mess. We do the lot in a single farm visit:

•  Whole-herd scan and physical verification
•  NAIT, Minda, myHERD and farm-system alignment
•  Replacement EID tags fitted on the spot
•  A clear variance report — easy to read, ready to action
•  Clean, corrected herd data uploaded back into your system

One visit. Sorted.

See your price for the job in two minutes — measuredlivestock.com

#NZFarming #DairyNZ #BeefNZ #NAIT #HerdData #MeasuredLivestock`;

  const handleCopy = () => {
    navigator.clipboard.writeText(adCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const heroImg = 'https://images.unsplash.com/photo-1709733971576-8caa9df3abf9?w=1600&q=80';

  // problem chip
  const problemChip = (text) => (
    <div style={{
      background: 'rgba(255,80,80,0.10)', border: '1.5px solid rgba(255,140,90,0.55)',
      padding: '12px 16px', borderRadius: 10, color: 'white',
      fontSize: 17, fontWeight: 600, textAlign: 'left',
      display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <span style={{
        width: 8, height: 8, borderRadius: 999, background: '#ff6b00',
        boxShadow: '0 0 0 3px rgba(255,107,0,0.25)', flexShrink: 0,
      }} />
      {text}
    </div>
  );
  const solutionChip = (text) => (
    <div style={{
      background: 'rgba(120,210,140,0.10)', border: '1.5px solid rgba(120,210,140,0.45)',
      padding: '12px 16px', borderRadius: 10, color: 'white',
      fontSize: 17, fontWeight: 600, textAlign: 'left',
      display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <span style={{
        width: 18, height: 18, borderRadius: 4, background: '#5fd07f',
        color: '#0f2818', fontWeight: 900, fontSize: 13, display: 'flex',
        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>✓</span>
      {text}
    </div>
  );

  return (
    <section className="bg-gradient-to-br from-[#0f2818] via-[#1a4d2e] to-[#0f2818] py-12">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-8">
          <span className="inline-block bg-[#ff6b00] text-white px-4 py-1 rounded-full text-xs uppercase tracking-widest font-bold mb-3">
            New · Problems-Solved Ad
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-2 flex items-center justify-center gap-3">
            <AlertTriangle className="w-9 h-9 text-[#ff6b00]" />
            Herd Data Sorted in One Visit
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto">
            Pain-point creative for dairy &amp; beef farmers — missing tags, NAIT mismatches, dirty records.
            Sold the way they buy: <em>problems solved</em>.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-8 items-start mb-8">
          {/* === PORTRAIT 1080 × 1350 === */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-white/90 text-sm font-semibold flex items-center gap-2">
                <ImageIcon className="w-4 h-4" /> FB Feed Portrait · 1080 × 1350
              </span>
              <button
                onClick={() => handleDownload(portraitRef, 'measuredlivestock-herd-data-sorted-1080x1350.png')}
                disabled={downloading === 'measuredlivestock-herd-data-sorted-1080x1350.png'}
                className="flex items-center gap-1.5 bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white text-sm font-semibold px-3 py-1.5 rounded transition-all disabled:opacity-60"
                data-testid="download-herd-sorted-portrait-btn"
              >
                <Download className="w-4 h-4" />
                {downloading === 'measuredlivestock-herd-data-sorted-1080x1350.png' ? 'Generating…' : 'Download PNG'}
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-2xl p-2 overflow-hidden">
              <div
                className="origin-top-left"
                style={{
                  transform: 'scale(0.42)',
                  width: 1080,
                  height: 1350,
                  marginRight: -1080 * 0.58,
                  marginBottom: -1350 * 0.58,
                }}
              >
                <div
                  ref={portraitRef}
                  style={{
                    width: 1080,
                    height: 1350,
                    position: 'relative',
                    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                    overflow: 'hidden',
                    backgroundImage: `linear-gradient(180deg, rgba(15,40,24,0.96) 0%, rgba(15,40,24,0.86) 38%, rgba(15,40,24,0.96) 100%), url(${heroImg})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    color: 'white',
                  }}
                >
                  {/* Brand corner */}
                  <div style={{
                    position: 'absolute', top: 38, left: 56, display: 'flex', alignItems: 'center', gap: 12,
                  }}>
                    <div style={{ width: 5, height: 26, background: '#ff6b00' }} />
                    <span style={{ fontSize: 13, letterSpacing: 5, fontWeight: 700, opacity: 0.92, textTransform: 'uppercase' }}>
                      Measured Livestock
                    </span>
                  </div>

                  {/* Top right pill */}
                  <div style={{
                    position: 'absolute', top: 32, right: 56,
                    background: 'rgba(255,107,0,0.12)', border: '1.5px solid #ff6b00',
                    color: '#ff8533', padding: '10px 22px', borderRadius: 999,
                    fontSize: 12, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase',
                  }}>
                    Dairy · Beef · NZ Wide
                  </div>

                  {/* Headline block */}
                  <div style={{ position: 'absolute', top: 130, left: 56, right: 56, textAlign: 'center' }}>
                    <div style={{ fontSize: 22, fontWeight: 600, opacity: 0.85, letterSpacing: 6, textTransform: 'uppercase', marginBottom: 18 }}>
                      Herd Data Headaches?
                    </div>
                    <h1 style={{
                      fontSize: 100, lineHeight: 0.98, fontWeight: 800, margin: 0,
                      letterSpacing: -2.4, textShadow: '0 6px 30px rgba(0,0,0,0.5)',
                    }}>
                      Sorted
                    </h1>
                    <h1 style={{
                      fontSize: 100, lineHeight: 0.98, fontWeight: 800, margin: '4px 0 22px',
                      color: '#ff8533', letterSpacing: -2.4,
                    }}>
                      In One Visit.
                    </h1>
                    <div style={{ fontSize: 24, opacity: 0.94, fontWeight: 400, lineHeight: 1.45, maxWidth: 840, margin: '0 auto' }}>
                      Missing tags. NAIT mismatches. Records that don't line up with what's
                      actually in the yard. We sort the mess in a single farm visit.
                    </div>
                  </div>

                  {/* Problems grid */}
                  <div style={{
                    position: 'absolute', top: 660, left: 56, right: 56,
                  }}>
                    <div style={{
                      fontSize: 13, fontWeight: 800, color: '#ff8533',
                      letterSpacing: 4, textTransform: 'uppercase', marginBottom: 14,
                    }}>
                      The mess we clean up
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      {problemChip('Missing EID tags')}
                      {problemChip('NAIT mismatches')}
                      {problemChip('Records ≠ yard reality')}
                      {problemChip('Years of admin backlog')}
                    </div>
                  </div>

                  {/* Solutions grid */}
                  <div style={{
                    position: 'absolute', top: 905, left: 56, right: 56,
                  }}>
                    <div style={{
                      fontSize: 13, fontWeight: 800, color: '#5fd07f',
                      letterSpacing: 4, textTransform: 'uppercase', marginBottom: 14,
                    }}>
                      What you walk away with
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      {solutionChip('Verified whole-herd record')}
                      {solutionChip('NAIT & system alignment')}
                      {solutionChip('Replacement tags fitted')}
                      {solutionChip('Clean upload back to you')}
                    </div>
                  </div>

                  {/* Bottom CTA */}
                  <div style={{
                    position: 'absolute', bottom: 56, left: 56, right: 56, textAlign: 'center',
                  }}>
                    <div style={{
                      background: '#ff6b00', color: 'white', padding: '24px 0', borderRadius: 10,
                      fontSize: 30, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
                      boxShadow: '0 14px 32px rgba(0,0,0,0.4)',
                    }}>
                      measuredlivestock.com
                    </div>
                    <div style={{ marginTop: 14, fontSize: 17, letterSpacing: 3, textTransform: 'uppercase', opacity: 0.85 }}>
                      See your price in two minutes
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* === LANDSCAPE 1200 × 630 === */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-white/90 text-sm font-semibold flex items-center gap-2">
                <ImageIcon className="w-4 h-4" /> FB Feed Landscape · 1200 × 630
              </span>
              <button
                onClick={() => handleDownload(landscapeRef, 'measuredlivestock-herd-data-sorted-1200x630.png')}
                disabled={downloading === 'measuredlivestock-herd-data-sorted-1200x630.png'}
                className="flex items-center gap-1.5 bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white text-sm font-semibold px-3 py-1.5 rounded transition-all disabled:opacity-60"
                data-testid="download-herd-sorted-landscape-btn"
              >
                <Download className="w-4 h-4" />
                {downloading === 'measuredlivestock-herd-data-sorted-1200x630.png' ? 'Generating…' : 'Download PNG'}
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-2xl p-2 overflow-hidden">
              <div
                className="origin-top-left"
                style={{
                  transform: 'scale(0.55)',
                  width: 1200,
                  height: 630,
                  marginRight: -1200 * 0.45,
                  marginBottom: -630 * 0.45,
                }}
              >
                <div
                  ref={landscapeRef}
                  style={{
                    width: 1200,
                    height: 630,
                    position: 'relative',
                    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                    overflow: 'hidden',
                    backgroundImage: `linear-gradient(110deg, rgba(15,40,24,0.96) 0%, rgba(15,40,24,0.90) 50%, rgba(15,40,24,0.55) 100%), url(${heroImg})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    color: 'white',
                  }}
                >
                  <div style={{ position: 'absolute', top: 36, left: 56, display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ width: 6, height: 32, background: '#ff6b00' }} />
                    <span style={{ fontSize: 13, letterSpacing: 5, fontWeight: 700, textTransform: 'uppercase', opacity: 0.92 }}>
                      Measured Livestock · Dairy &amp; Beef
                    </span>
                  </div>

                  <div style={{
                    position: 'absolute', top: 36, right: 56,
                    background: 'rgba(255,107,0,0.12)', border: '1.5px solid #ff6b00',
                    color: '#ff8533', padding: '8px 18px', borderRadius: 999,
                    fontSize: 12, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase',
                  }}>
                    Problems · Solved · NZ Wide
                  </div>

                  {/* Left text */}
                  <div style={{ position: 'absolute', top: 120, left: 56, right: 480 }}>
                    <div style={{ fontSize: 22, fontWeight: 600, opacity: 0.88, marginBottom: 8, letterSpacing: 4, textTransform: 'uppercase' }}>
                      Herd Data Headaches?
                    </div>
                    <h1 style={{
                      fontSize: 78, lineHeight: 0.96, fontWeight: 800, margin: 0,
                      letterSpacing: -1.8,
                    }}>
                      Sorted
                    </h1>
                    <h1 style={{
                      fontSize: 78, lineHeight: 0.96, fontWeight: 800, margin: '4px 0 18px',
                      color: '#ff8533', letterSpacing: -1.8,
                    }}>
                      In One Visit.
                    </h1>
                    <div style={{ fontSize: 18, fontWeight: 400, opacity: 0.94, lineHeight: 1.5, maxWidth: 660 }}>
                      Missing tags. NAIT mismatches. Records that don't line up with what's
                      actually in the yard — we clean the mess up and upload verified data
                      straight back to you.
                    </div>
                  </div>

                  {/* Right column — solutions stack */}
                  <div style={{
                    position: 'absolute', top: 120, right: 56, width: 400,
                  }}>
                    <div style={{
                      fontSize: 12, fontWeight: 800, color: '#5fd07f',
                      letterSpacing: 4, textTransform: 'uppercase', marginBottom: 12,
                    }}>
                      What you walk away with
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {['Whole-herd verified', 'NAIT & system aligned', 'Tags replaced on the spot', 'Clean data back to you'].map((t) => (
                        <div key={t} style={{
                          background: 'rgba(120,210,140,0.10)', border: '1.5px solid rgba(120,210,140,0.45)',
                          padding: '10px 14px', borderRadius: 8, fontSize: 15, fontWeight: 600,
                          display: 'flex', alignItems: 'center', gap: 10,
                        }}>
                          <span style={{
                            width: 16, height: 16, borderRadius: 4, background: '#5fd07f',
                            color: '#0f2818', fontWeight: 900, fontSize: 12, display: 'flex',
                            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          }}>✓</span>
                          {t}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA */}
                  <div style={{
                    position: 'absolute', bottom: 36, left: 56, right: 56,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24,
                  }}>
                    <div>
                      <div style={{ fontSize: 13, letterSpacing: 3, textTransform: 'uppercase', opacity: 0.85, marginBottom: 4 }}>
                        See your price in two minutes
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 800 }}>measuredlivestock.com</div>
                    </div>
                    <div style={{
                      background: '#ff6b00', color: 'white', padding: '18px 32px', borderRadius: 8,
                      fontSize: 20, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
                      boxShadow: '0 10px 28px rgba(0,0,0,0.4)',
                    }}>
                      Book a Sort →
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Caption */}
        <div className="bg-white rounded-xl p-6 shadow-2xl">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="font-bold text-[#1a4d2e] flex items-center gap-2 text-lg">
              <AlertTriangle className="w-5 h-5 text-[#ff6b00]" />
              Ready-to-Post Caption
            </h3>
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 bg-[#1a4d2e] hover:bg-[#1a4d2e]/90 text-white px-4 py-2 rounded-lg text-sm font-semibold"
              data-testid="copy-herd-sorted-ad-copy-btn"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy Caption'}
            </button>
          </div>
          <pre className="whitespace-pre-wrap text-sm text-[#4a4a4a] bg-[#f7f5f1] border border-[#e5ddd5] p-4 rounded-lg leading-relaxed font-sans">
{adCopy}
          </pre>
          <p className="text-xs text-[#8d6e63] mt-3">
            💡 <strong>Targeting tip:</strong> Run as a <strong>traffic / conversion campaign</strong> to PriceHub.
            Audience: <em>Dairy farmers, Beef cattle producers, NAIT account holders, herd managers</em>. Location:
            <strong> New Zealand</strong>. Best around end-of-season / pre-mating when records pain peaks.
          </p>
        </div>
      </div>
    </section>
  );
};

export default HerdDataSortedAd;
